// author: Caleb Yearsley (caleb.yearsley@tecuity.com)
// date: 2019-01-14

import "./AuthorizedComponent.less";

import React, { useEffect, useState } from "react";

import { CancellationToken } from "lib/api/base";
import { Permission } from "lib/data/model";
import { LoginService, PermissionService, StateService } from "lib/services";
import { Event, EventDispatcher } from "lib/EventDispatcher";

interface IAuthorizedComponentProps<T extends React.ReactNode = React.ReactElement> {
	permission?: Permission;
	children: T;
	notAuthorizedChildren?: React.ReactNode;
	hideNotAuthorizedUntilLoaded?: boolean;
}

const AuthorizedComponentFC = <T extends React.ReactNode>(props: React.PropsWithChildren<IAuthorizedComponentProps<T>>) => {

	const [cancellationToken] = useState(new CancellationToken());
	const [isLoading, setLoading] = useState(true);
	const [isAuthorized, setAuthorized] = useState(false);

	useEffect(() => {

		const checkCurrentAuthorization = async () => {

			const isCurrentlyAuthorized = await checkAuthorization(props.permission);

			StateService.setCancelableState(() => setLoading(false), cancellationToken);
			StateService.setCancelableState(() => setAuthorized(isCurrentlyAuthorized), cancellationToken);
		};

		const onLoggedIn = () => {

			checkCurrentAuthorization();
		};

		const onLoggedOut = () => {

			StateService.setCancelableState(() => setLoading(false), cancellationToken);
			StateService.setCancelableState(() => setAuthorized(false), cancellationToken);
		};

		const onUnauthorized = () => {

			StateService.setCancelableState(() => setLoading(false), cancellationToken);
			StateService.setCancelableState(() => setAuthorized(false), cancellationToken);
		};

		EventDispatcher.register(Event.LoggedIn, onLoggedIn);
		EventDispatcher.register(Event.LoggedOut, onLoggedOut);
		EventDispatcher.register(Event.Unauthorized, onUnauthorized);

		checkCurrentAuthorization();

		return () => {

			cancellationToken.cancel();

			EventDispatcher.unregister(Event.LoggedIn, onLoggedIn);
			EventDispatcher.unregister(Event.LoggedOut, onLoggedOut);
			EventDispatcher.unregister(Event.Unauthorized, onUnauthorized);
		};
	}, []);

	if (!isLoading && isAuthorized) {

		// Render Children
		return (

			<React.Fragment>
				{props.children}
			</React.Fragment>
		);
	}
	else if (props.notAuthorizedChildren !== undefined) {

		if (props.hideNotAuthorizedUntilLoaded && isLoading) {
			return null;
		}

		return (
			<React.Fragment>
				{props.notAuthorizedChildren}
			</React.Fragment>
		);
	}
	else {

		// Render Nothing
		return null;
	}
};

const checkAuthorization = async (permission?: Permission): Promise<boolean> => {

	const isLoggedIn = await LoginService.isLoggedIn();

	if (isLoggedIn) {

		const loggedInUser = await LoginService.getUserIfExists();

		if (loggedInUser !== undefined) {

			if (permission !== undefined) {

				const isPermitted = await PermissionService.userHasPermission(loggedInUser, permission);

				return isPermitted;
			}
			else {

				return true;
			}
		}
	}

	return false;
};

export class AuthorizedComponent<T extends React.ReactNode = React.ReactElement> extends React.Component<IAuthorizedComponentProps<T>, any> {

	public render() {

		return <AuthorizedComponentFC {...this.props} />;
	}
}
